import React, { useRef, useEffect, useState, useCallback, useMemo } from "react";
import "./RichTextEditor.css";

const normalizeHtml = (content) => {
  if (!content) return "";
  const str = String(content).trim();
  if (!str) return "";
  // If content already contains HTML tags, return as is
  if (/<[a-z][\s\S]*>/i.test(str)) {
    return str;
  }
  // Otherwise convert legacy newline-separated plain text into paragraph tags
  return str
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => `<p>${line}</p>`)
    .join("");
};

const hasVisibleContent = (htmlOrText) => {
  if (!htmlOrText) return false;
  const stripped = String(htmlOrText).replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
  return stripped.length > 0;
};

const escapeHtml = (text) => {
  const map = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#039;",
  };
  return String(text).replace(/[&<>"']/g, (m) => map[m]);
};

const getStats = (htmlOrText) => {
  if (!htmlOrText) return { words: 0, chars: 0 };
  const text = String(htmlOrText).replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ").trim();
  if (!text) return { words: 0, chars: 0 };
  const words = text.split(/\s+/).filter(Boolean).length;
  const chars = text.length;
  return { words, chars };
};

const RichTextEditor = ({
  value = "",
  onChange = () => {},
  placeholder = "Enter terms & conditions...",
  required = false,
  className = "",
  style = {},
  minHeight = "150px",
}) => {
  const editorRef = useRef(null);
  const lastSyncedHtmlRef = useRef("");
  const debounceTimerRef = useRef(null);
  const pendingValueRef = useRef(null);

  const [activeFormats, setActiveFormats] = useState({
    bold: false,
    italic: false,
    underline: false,
    strikeThrough: false,
    insertUnorderedList: false,
    insertOrderedList: false,
    justifyLeft: false,
    justifyCenter: false,
    justifyRight: false,
    blockTag: "p",
  });

  const [stats, setStats] = useState(() => getStats(value));

  // Check if editor or any descendant currently has focus
  const isEditorFocused = useCallback(() => {
    if (!editorRef.current) return false;
    const active = document.activeElement;
    return active === editorRef.current || editorRef.current.contains(active);
  }, []);

  // Flush debounced change immediately
  const flushChange = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    if (pendingValueRef.current !== null) {
      const nextVal = pendingValueRef.current;
      pendingValueRef.current = null;
      lastSyncedHtmlRef.current = nextVal;
      onChange(nextVal);
    }
  }, [onChange]);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Sync external value to innerHTML when not actively editing
  useEffect(() => {
    if (!editorRef.current) return;
    const normalized = normalizeHtml(value);

    // Only update innerHTML if not focused and the value is genuinely different
    if (!isEditorFocused() && normalized !== lastSyncedHtmlRef.current) {
      lastSyncedHtmlRef.current = normalized;
      if (editorRef.current.innerHTML !== normalized) {
        editorRef.current.innerHTML = normalized;
        setStats(getStats(normalized));
      }
    }
  }, [value, isEditorFocused]);

  // Check and update active formats only when they actually change
  const updateActiveStates = useCallback(() => {
    if (!editorRef.current) return;
    try {
      const isBold = document.queryCommandState("bold");
      const isItalic = document.queryCommandState("italic");
      const isUnderline = document.queryCommandState("underline");
      const isStrike = document.queryCommandState("strikeThrough");
      const isUl = document.queryCommandState("insertUnorderedList");
      const isOl = document.queryCommandState("insertOrderedList");
      const isLeft = document.queryCommandState("justifyLeft");
      const isCenter = document.queryCommandState("justifyCenter");
      const isRight = document.queryCommandState("justifyRight");

      let currentBlock = "p";
      const selection = window.getSelection();
      if (selection && selection.anchorNode) {
        let node = selection.anchorNode;
        while (node && node !== editorRef.current) {
          const nodeName = node.nodeName ? node.nodeName.toLowerCase() : "";
          if (["h1", "h2", "h3", "h4", "h5", "h6", "p"].includes(nodeName)) {
            currentBlock = nodeName;
            break;
          }
          node = node.parentNode;
        }
      }

      setActiveFormats((prev) => {
        if (
          prev.bold === isBold &&
          prev.italic === isItalic &&
          prev.underline === isUnderline &&
          prev.strikeThrough === isStrike &&
          prev.insertUnorderedList === isUl &&
          prev.insertOrderedList === isOl &&
          prev.justifyLeft === isLeft &&
          prev.justifyCenter === isCenter &&
          prev.justifyRight === isRight &&
          prev.blockTag === currentBlock
        ) {
          return prev; // Skip state update to prevent re-renders
        }
        return {
          bold: isBold,
          italic: isItalic,
          underline: isUnderline,
          strikeThrough: isStrike,
          insertUnorderedList: isUl,
          insertOrderedList: isOl,
          justifyLeft: isLeft,
          justifyCenter: isCenter,
          justifyRight: isRight,
          blockTag: currentBlock,
        };
      });
    } catch (e) {
      // Ignore queryCommandState issues in edge environments
    }
  }, []);

  // Handle user input inside editor
  const handleInput = useCallback(() => {
    if (!editorRef.current) return;
    const html = editorRef.current.innerHTML;
    const hasContent = hasVisibleContent(html);
    const resultValue = hasContent ? html : "";

    setStats(getStats(html));
    pendingValueRef.current = resultValue;

    // Debounce notifying parent to keep 60fps typing responsiveness in large forms
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      flushChange();
    }, 250);

    updateActiveStates();
  }, [flushChange, updateActiveStates]);

  // Execute a document command
  const executeCommand = useCallback(
    (command, val = null) => {
      if (editorRef.current) {
        editorRef.current.focus();
      }
      try {
        document.execCommand(command, false, val);
      } catch (err) {
        // Fallback or ignore
      }
      handleInput();
    },
    [handleInput]
  );

  // Change block format (p, h1, h2, h3, h4)
  const handleBlockTagChange = useCallback(
    (e) => {
      const tag = e.target.value;
      if (editorRef.current) {
        editorRef.current.focus();
      }
      try {
        document.execCommand("formatBlock", false, `<${tag}>`);
      } catch (err) {
        document.execCommand("formatBlock", false, tag);
      }
      handleInput();
    },
    [handleInput]
  );

  // Clean paste handler (strips unwanted Word/Google Docs styling)
  const handlePaste = useCallback(
    (e) => {
      e.preventDefault();
      const text = (e.clipboardData || window.clipboardData).getData("text/plain");
      if (!text) return;

      const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
      if (lines.length > 1) {
        const cleanHtml = lines.map((line) => `<p>${escapeHtml(line)}</p>`).join("");
        document.execCommand("insertHTML", false, cleanHtml);
      } else {
        document.execCommand("insertText", false, text);
      }
      handleInput();
    },
    [handleInput]
  );

  // Keyboard shortcuts (Ctrl+B, Ctrl+I, Ctrl+U, Tab for lists)
  const handleKeyDown = useCallback(
    (e) => {
      const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
      const cmdOrCtrl = isMac ? e.metaKey : e.ctrlKey;

      if (cmdOrCtrl) {
        const key = e.key.toLowerCase();
        if (key === "b") {
          e.preventDefault();
          executeCommand("bold");
        } else if (key === "i") {
          e.preventDefault();
          executeCommand("italic");
        } else if (key === "u") {
          e.preventDefault();
          executeCommand("underline");
        } else if (key === "z") {
          e.preventDefault();
          if (e.shiftKey) {
            executeCommand("redo");
          } else {
            executeCommand("undo");
          }
        } else if (key === "y") {
          e.preventDefault();
          executeCommand("redo");
        }
      } else if (e.key === "Tab") {
        // Indent or outdent if in a list
        if (activeFormats.insertUnorderedList || activeFormats.insertOrderedList) {
          e.preventDefault();
          if (e.shiftKey) {
            executeCommand("outdent");
          } else {
            executeCommand("indent");
          }
        }
      }
    },
    [activeFormats, executeCommand]
  );

  // Insert terms & conditions sample template
  const insertSampleTemplate = useCallback(() => {
    const template = `<h3>TERMS & CONDITIONS</h3><ul><li>Applicable on all digital menu orders.</li><li>Discounts cannot be combined with other promotional offers.</li><li>All prices are inclusive of applicable sales taxes.</li></ul>`;
    if (editorRef.current) {
      editorRef.current.innerHTML = template;
      editorRef.current.focus();
    }
    lastSyncedHtmlRef.current = template;
    pendingValueRef.current = null;
    onChange(template);
    setStats(getStats(template));
    updateActiveStates();
  }, [onChange, updateActiveStates]);

  const isContentValid = useMemo(() => hasVisibleContent(value), [value]);

  return (
    <div className={`rich-editor-wrapper ${className}`} style={style}>
      {/* Modern Toolbar */}
      <div className="rich-editor-toolbar">
        {/* Block / Heading Selector */}
        <div className="rich-editor-group">
          <select
            className="rich-editor-select"
            value={activeFormats.blockTag}
            onChange={handleBlockTagChange}
            title="Text Style (Heading or Paragraph)"
          >
            <option value="p">Normal Text</option>
            <option value="h1">Heading 1</option>
            <option value="h2">Heading 2</option>
            <option value="h3">Heading 3</option>
            <option value="h4">Heading 4</option>
          </select>
        </div>

        <div className="rich-editor-divider" />

        {/* Inline Formatting */}
        <div className="rich-editor-group">
          <button
            type="button"
            className={`rich-editor-btn ${activeFormats.bold ? "active" : ""}`}
            onMouseDown={(e) => {
              e.preventDefault();
              executeCommand("bold");
            }}
            title="Bold (Ctrl+B)"
          >
            <i className="fa fa-bold" />
          </button>
          <button
            type="button"
            className={`rich-editor-btn ${activeFormats.italic ? "active" : ""}`}
            onMouseDown={(e) => {
              e.preventDefault();
              executeCommand("italic");
            }}
            title="Italic (Ctrl+I)"
          >
            <i className="fa fa-italic" />
          </button>
          <button
            type="button"
            className={`rich-editor-btn ${activeFormats.underline ? "active" : ""}`}
            onMouseDown={(e) => {
              e.preventDefault();
              executeCommand("underline");
            }}
            title="Underline (Ctrl+U)"
          >
            <i className="fa fa-underline" />
          </button>
          <button
            type="button"
            className={`rich-editor-btn ${activeFormats.strikeThrough ? "active" : ""}`}
            onMouseDown={(e) => {
              e.preventDefault();
              executeCommand("strikeThrough");
            }}
            title="Strikethrough"
          >
            <i className="fa fa-strikethrough" />
          </button>
        </div>

        <div className="rich-editor-divider" />

        {/* Lists */}
        <div className="rich-editor-group">
          <button
            type="button"
            className={`rich-editor-btn ${activeFormats.insertUnorderedList ? "active" : ""}`}
            onMouseDown={(e) => {
              e.preventDefault();
              executeCommand("insertUnorderedList");
            }}
            title="Bullet List"
          >
            <i className="fa fa-list-ul" />
          </button>
          <button
            type="button"
            className={`rich-editor-btn ${activeFormats.insertOrderedList ? "active" : ""}`}
            onMouseDown={(e) => {
              e.preventDefault();
              executeCommand("insertOrderedList");
            }}
            title="Numbered List"
          >
            <i className="fa fa-list-ol" />
          </button>
        </div>

        <div className="rich-editor-divider" />

        {/* Alignments */}
        <div className="rich-editor-group">
          <button
            type="button"
            className={`rich-editor-btn ${activeFormats.justifyLeft ? "active" : ""}`}
            onMouseDown={(e) => {
              e.preventDefault();
              executeCommand("justifyLeft");
            }}
            title="Align Left"
          >
            <i className="fa fa-align-left" />
          </button>
          <button
            type="button"
            className={`rich-editor-btn ${activeFormats.justifyCenter ? "active" : ""}`}
            onMouseDown={(e) => {
              e.preventDefault();
              executeCommand("justifyCenter");
            }}
            title="Align Center"
          >
            <i className="fa fa-align-center" />
          </button>
          <button
            type="button"
            className={`rich-editor-btn ${activeFormats.justifyRight ? "active" : ""}`}
            onMouseDown={(e) => {
              e.preventDefault();
              executeCommand("justifyRight");
            }}
            title="Align Right"
          >
            <i className="fa fa-align-right" />
          </button>
        </div>

        <div className="rich-editor-divider" />

        {/* History & Clear */}
        <div className="rich-editor-group">
          <button
            type="button"
            className="rich-editor-btn"
            onMouseDown={(e) => {
              e.preventDefault();
              executeCommand("undo");
            }}
            title="Undo (Ctrl+Z)"
          >
            <i className="fa fa-undo" />
          </button>
          <button
            type="button"
            className="rich-editor-btn"
            onMouseDown={(e) => {
              e.preventDefault();
              executeCommand("redo");
            }}
            title="Redo (Ctrl+Y)"
          >
            <i className="fa fa-repeat" />
          </button>
          <button
            type="button"
            className="rich-editor-btn"
            onMouseDown={(e) => {
              e.preventDefault();
              executeCommand("removeFormat");
            }}
            title="Clear Formatting"
          >
            <i className="fa fa-eraser" />
          </button>
        </div>

        {/* Sample Template Button */}
        <button
          type="button"
          className="rich-editor-template-btn"
          onMouseDown={(e) => {
            e.preventDefault();
            insertSampleTemplate();
          }}
          title="Insert Heading & Bullet Template"
        >
          <i className="fa fa-magic mr-1" />
          Sample Template
        </button>
      </div>

      {/* Content Editable Area */}
      <div
        ref={editorRef}
        className="rich-editor-content"
        contentEditable
        data-placeholder={placeholder}
        style={{ minHeight }}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onKeyUp={updateActiveStates}
        onMouseUp={updateActiveStates}
        onFocus={updateActiveStates}
        onBlur={flushChange}
      />

      {/* Footer / Stats Bar */}
      <div className="rich-editor-footer">
        <div className="rich-editor-tip">
          <i className="fa fa-info-circle mr-1" />
          Use headings for titles & bullets for clear terms
        </div>
        <div className="rich-editor-stats">
          {stats.words} {stats.words === 1 ? "word" : "words"} &middot; {stats.chars} chars
        </div>
      </div>

      {/* Hidden input for HTML5 form validation when required */}
      {required && (
        <input
          type="text"
          required={required}
          value={isContentValid ? "valid" : ""}
          onChange={() => {}}
          tabIndex={-1}
          style={{
            position: "absolute",
            opacity: 0,
            height: 0,
            width: 0,
            pointerEvents: "none",
            bottom: 0,
            left: "50%",
          }}
        />
      )}
    </div>
  );
};

export default React.memo(RichTextEditor);
